import React, { useState } from "react";
import "../styles/QRCodeDisplay.css";

interface QRCodeDisplayProps {
  qrImage: string;
  encodedData: string;
  userId: string;
  onDownload?: () => void;
  onRegenerate?: () => void;
  loading?: boolean;
}

// Check if Web Share API is available
const canShare = typeof navigator !== "undefined" && "share" in navigator;

export function QRCodeDisplay({
  qrImage,
  encodedData,
  userId,
  onDownload,
  onRegenerate,
  loading = false,
}: QRCodeDisplayProps) {
  const [showDetails, setShowDetails] = useState(false);

  const handleDownload = () => {
    if (!qrImage) return;

    // Create a temporary link to download the image
    const link = document.createElement("a");
    link.href = qrImage;
    link.download = `gavekort-qr-${userId}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    onDownload?.();
  };

  const handleShare = async () => {
    if (navigator.share && qrImage) {
      try {
        // Convert data URL to blob
        const response = await fetch(qrImage);
        const blob = await response.blob();
        const file = new File([blob], `gavekort-qr-${userId}.png`, {
          type: "image/png",
        });

        await navigator.share({
          title: "GaveKort Account QR Code",
          text: "Scan this QR code to process a payment to my account",
          files: [file],
        });
      } catch (error) {
        console.error("Share failed:", error);
      }
    }
  };

  return (
    <div className="qr-code-container">
      <div className="qr-header">
        <h3>📱 Your Account QR Code</h3>
        <p className="qr-subtitle">
          Share this QR code for instant payments and account access
        </p>
      </div>

      <div className="qr-content">
        {loading ? (
          <div className="qr-loading">
            <div className="spinner"></div>
            <p>Generating QR code...</p>
          </div>
        ) : (
          <>
            <div className="qr-image-container">
              <img src={qrImage} alt="Account QR Code" className="qr-image" />
              <div className="qr-scan-hint">
                <span>📸</span>
                <span>Scan with any QR code reader</span>
              </div>
            </div>

            <div className="qr-actions">
              <button className="btn btn-primary" onClick={handleDownload} title="Download QR Code">
                ⬇️ Download
              </button>
              {canShare && (
                <button className="btn btn-secondary" onClick={handleShare} title="Share QR Code">
                  🔗 Share
                </button>
              )}
              {onRegenerate && (
                <button className="btn btn-secondary" onClick={onRegenerate} title="Generate new QR code">
                  🔄 Regenerate
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => setShowDetails(!showDetails)}
                title="Show technical details"
              >
                ℹ️ Details
              </button>
            </div>

            {showDetails && (
              <div className="qr-details">
                <h4>Technical Details</h4>
                <div className="detail-item">
                  <label>Encoded Data (base64|signature):</label>
                  <code className="monospace">{encodedData.substring(0, 60)}...</code>
                </div>
                <div className="detail-item">
                  <label>User ID:</label>
                  <code className="monospace">{userId}</code>
                </div>
                <p className="detail-note">
                  ⚠️ Keep this QR code secure. Anyone with access to it can process payments from your account.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
